<script setup lang="ts">
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { onBeforeUnmount, watch, ref, computed } from "vue";

const emit = defineEmits<{
  (e: 'rewatch'): void;
}>();

const { autoAdvance } = useUserPreferences();
const {
  course,
  getNextActivity,
  getActivityUrl,
  isCourse,
} = useCourseState();
const { t } = useI18n();
const route = useRoute();
const localePath = useLocalePath();

const nextActivity = computed(() => {
  const currentActivityId = route.params.quizId || route.params.lessonId;
  return getNextActivity(currentActivityId as string);
});

const nextActivityPath = computed(() => localePath(getActivityUrl(nextActivity.value) as string));

const autoAdvanceTimeout = ref<ReturnType<typeof setTimeout>>();

const onRewatch = () => {
  clearTimeout(autoAdvanceTimeout.value);
  emit('rewatch');
};

const onNext = () => {
  clearTimeout(autoAdvanceTimeout.value);

  if (!nextActivityPath.value) {
    return;
  }

  navigateTo(nextActivityPath.value, {
    replace: true,
  });
};

onBeforeMount(() => {
  if (autoAdvance.value) {
    autoAdvanceTimeout.value = setTimeout(onNext, 8000);
  }
});

watch(route, () => {
  clearTimeout(autoAdvanceTimeout.value);
});

onBeforeUnmount(() => {
  clearTimeout(autoAdvanceTimeout.value);
});
</script>

<template>
  <div class="flex items-center aspect-video justify-center bg-neutral-100 lg:rounded-2xl">
    <div class="flex flex-col justify-between gap-4 p-6 w-full h-full bg-neutral-5 lg:rounded-2xl lg:max-w-[25rem] lg:max-h-[15rem]">
      <div>
        <div class="text-14 uppercase tracking-widest text-neutral-80 font-bold mb-4">
          {{nextActivity ? 'Up Next' : 'Congratulations!'}}
        </div>

        <div class="text-20 font-bold text-neutral-100">
          <template v-if="nextActivity">
            {{nextActivity?.title}}
          </template>

          <template v-else>
            {{t('course.lesson.finished.note', {
              activityType: t(`course.lesson.type.${isCourse ? 'lesson' : 'case'}`),
              courseType: t(`course.general.type.${course?.type}`)
            })}}
          </template>
        </div>
      </div>

      <div class="flex items-center justify-end gap-2">
        <Button
          text
          severity="secondary"
          @click="onRewatch"
        >
          <div class="flex items-center gap-2 text-16 font-bold z-[1]">
            <FontAwesomeIcon icon="fa-arrow-rotate-left" />
            <div>{{t('course.lesson.finished.button.rewatch')}}</div>
          </div>
        </Button>

        <NuxtLink :to="nextActivityPath">
          <!-- todo: check we really need :href="nextActivityPath" (there's already a <NuxtLink/> wrapper above ) -->
          <Button
            class="relative overflow-clip border-none px-8 bg-neutral-20 text-neutral-90"
            @click="onNext()"
          >
            <div
              class="bg-blue-30 w-0 h-full absolute top-0 left-0 transition-all"
              :class="{'animate-fillHorizontal': nextActivityPath && autoAdvance}"
            />

            <div class="flex items-center gap-2 text-16 font-bold z-[1]">
              <div>
                {{nextActivityPath ? t('global.button.next') : t('global.button.continue')}}
              </div>
              <FontAwesomeIcon icon="fa-forward" />
            </div>
          </Button>
        </NuxtLink>
      </div>
    </div>
  </div>
</template>